/**
 * Shared utility functions for shift operations
 */

/**
 * Parses a composite shift ID (format: "requestId-seqId") into its components
 * @param compositeId - The composite shift ID string
 * @returns Object containing requestId and requestSeqId as numbers
 */
export const parseShiftId = (compositeId: string): { requestId: number; requestSeqId: number } => {
    const [reqId, seqId] = compositeId.split('-');
    return {
        requestId: Number.parseInt(reqId, 10) || 0,
        requestSeqId: Number.parseInt(seqId, 10) || 0,
    };
};

/**
 * Creates a composite shift ID from request ID and sequence ID
 * @param requestId - The request ID
 * @param seqId - The sequence ID
 * @returns Composite ID string in format "requestId-seqId"
 */
export const createCompositeShiftId = (requestId: number | string, seqId: number | string): string => {
    return `${requestId}-${seqId}`;
};

/**
 * Validates if a composite shift ID is in the correct format
 * @param compositeId - The composite shift ID to validate
 * @returns True if valid, false otherwise
 */
export const isValidCompositeShiftId = (compositeId: string): boolean => {
    const parts = compositeId.split('-');
    if (parts.length !== 2) return false;
    
    const [reqId, seqId] = parts;
    const requestId = Number.parseInt(reqId, 10);
    const requestSeqId = Number.parseInt(seqId, 10);
    
    return !Number.isNaN(requestId) && !Number.isNaN(requestSeqId) && requestId > 0 && requestSeqId > 0;
};

type SortableShift = {
    date?: string;
    requestDt?: string;
    requestDate?: string;
    startTime?: string;
};

const getShiftDateTime = (shift: SortableShift): number => {
    const rawDate = shift.date || shift.requestDt || shift.requestDate || '';
    if (!rawDate) return Number.MAX_SAFE_INTEGER;

    const dateOnlyMatch = /^\d{4}-\d{2}-\d{2}/.exec(rawDate);
    const dateOnly = dateOnlyMatch ? dateOnlyMatch[0] : rawDate;
    const rawTime = shift.startTime && /^\d{2}:\d{2}/.test(shift.startTime)
        ? shift.startTime.slice(0, 5)
        : '00:00';
    const timestamp = new Date(`${dateOnly}T${rawTime}:00`).getTime();

    if (!Number.isNaN(timestamp)) return timestamp;

    const fallbackTimestamp = new Date(rawDate).getTime();
    return Number.isNaN(fallbackTimestamp) ? Number.MAX_SAFE_INTEGER : fallbackTimestamp;
};

export const sortShiftsChronologically = <T extends SortableShift>(shifts: T[]): T[] =>
    [...shifts].sort((a, b) => getShiftDateTime(a) - getShiftDateTime(b));